// ─────────────────────────────────────────────────────────────
// LegalDocument
// Extracted from App.js.
//
// PRIVACY_VERSION / TERMS_VERSION are the source of truth for document
// versioning. When you publish a material change to either document,
// bump the corresponding constant. The new value gets recorded in
// profiles.agreed_to_privacy_version / agreed_to_terms_version when
// users sign up, so we always know which version of the documents each
// user originally consented to.
//
// Format: ISO date "YYYY-MM-DD". Use the day the new version goes live.
// ─────────────────────────────────────────────────────────────

import { C } from "../../lib/colors";

export const PRIVACY_VERSION = "2026-05-25";
export const TERMS_VERSION   = "2026-06-05";

// Human-readable label for the document bodies, derived from the version
// constants so the date inside the document text can't drift out of sync
// with what we record in the database.
function formatVersionLabel(isoDate) {
  const [y, m, d] = isoDate.split("-").map(Number);
  const monthName = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"][m - 1];
  return `${monthName} ${d}, ${y}`;
}

export function LegalDocument({ title }) {
  const privacyContent = `# Privacy Policy

**Futures OS**
Last Updated: ${formatVersionLabel(PRIVACY_VERSION)}

---

## 1. Introduction

Futures OS ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our trading journal and analytics platform ("Service").

By using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree, please discontinue use of the Service.

---

## 2. Information We Collect

### Information You Provide Directly
- **Account Information:** Email address, display name, and password used to create your account.
- **Trade Data:** Trade entries including symbols, entry/exit prices, position sizes, profit and loss figures, dates, and times.
- **Behavioral & Psychological Data:** Self-reported data including emotional state, discipline scores, focus levels, fear and greed ratings, rule adherence scores, and post-trade reflections.
- **Account Settings:** Trading account names, account types, starting balances, maximum drawdown limits, and risk preferences.
- **Custom Strategies:** Strategy names, descriptions, and preferences you create within the Service.
- **Imported Data:** Trade data imported via CSV from brokers such as TopStep, Tradovate, or other supported platforms.

### Information Collected Automatically
- **Usage Data:** Pages visited, features used, session duration, and interaction patterns within the Service.
- **Device Information:** Browser type, operating system, and IP address.
- **Authentication Tokens:** Session tokens used to maintain your logged-in state.

---

## 3. How We Use Your Information

We use the information we collect to:

- **Provide the Service:** Display your trade journal, analytics, discipline scores, and performance metrics.
- **Generate AI-Powered Analysis (Planned):** We are building AI-powered features that will create personalized daily briefings and coaching insights using anonymized versions of your trade and behavioral data. These features are not yet active. When they launch, the disclosures in Section 6 will apply, and you will be able to opt out before any data is sent to third-party AI processors.
- **Persist Your Settings:** Save your preferences, risk limits, custom strategies, and account configurations across sessions.
- **Improve the Service:** Analyze aggregate, anonymized usage patterns to improve features and performance.
- **Communicate With You:** Send transactional emails related to your account (password resets, billing receipts). We do not send marketing emails without your explicit consent.
- **Ensure Security:** Detect and prevent fraudulent or unauthorized access.

---

## 4. Data Storage and Security

- Your data is stored in **Supabase**, a secure cloud database platform. Supabase uses industry-standard encryption (AES-256 at rest, TLS in transit) and is SOC 2 compliant.
- Row-Level Security (RLS) is enforced on all data tables, meaning your data is only accessible to your authenticated account.
- We do not store payment card information. Payment processing is handled by a PCI-compliant third-party processor.
- **Data retention.** We retain your data only as long as we have a basis to do so:
  - **While your subscription is active or in a free trial:** your data is retained for as long as you have access.
  - **If you cancel your subscription:** your data is preserved for **150 days** after your access ends, during which you can resubscribe and pick up exactly where you left off. On day 150 your account is automatically scheduled for deletion (the same "soft delete" state described below). On day 180 your data is permanently and irreversibly deleted from our systems.
  - **If you delete your account directly** (Profile → Danger Zone → Delete my account): your data enters a 30-day grace period. Sign in within those 30 days to restore. After 30 days, your data is permanently deleted.
  - **Restoration during the grace period:** signing in shows a recovery screen with a "Restore my account" button. After deletion runs, restoration is no longer possible.
  - **Hard deletion is permanent.** Once the purge has run, we cannot recover your data from backups for the purpose of restoring your account.

---

## 5. Data Sharing and Disclosure

We do not sell your personal data. We do not share your data with advertisers.

We may share your data only in the following limited circumstances:

- **Service Providers:** Third-party vendors who assist us in operating the Service (listed in Section 6), bound by confidentiality agreements.
- **Legal Requirements:** If required by law, court order, or governmental authority.
- **Business Transfer:** If Futures OS is acquired or merges with another company, your data may be transferred as part of that transaction. You will be notified in advance.
- **With Your Consent:** Any other sharing will only occur with your explicit consent.

---

## 6. Third-Party AI Processing (Planned — Not Currently Active)

Futures OS plans to integrate **Anthropic's Claude API** to generate personalized AI-powered trade analysis and daily briefings. **This integration is not currently active.** Your data is not being sent to Anthropic or any other AI processor at this time.

This section describes how AI processing will work when launched, so you can review it in advance of any features going live.

**What will be sent to Anthropic (when active):**
A subset of your trade and behavioral data will be sent to Anthropic's API for processing. This will include:
- Aggregated trade statistics (win rates, P&L totals, discipline score trends)
- Behavioral pattern summaries (e.g., win rate by mental state, best-performing setups)
- Risk settings (max drawdown, max daily trades)
- Recent trade history (symbols, results, scores — no personally identifying information)

**What will NOT be sent to Anthropic:**
- Your name or email address
- Raw account balances or broker credentials
- Payment information

**Anthropic's Data Practices:**
- Anthropic does **not** use API-submitted data to train its models by default.
- Data submitted via the API is subject to Anthropic's Privacy Policy and Usage Policy.
- Anthropic is listed as a **planned data sub-processor** under this policy.

**Your Control:**
- AI-powered analysis features will be opt-out by default in your account settings when launched.
- We will not begin sending data to Anthropic without an in-product notice. Users will be able to disable AI features before any processing occurs.

---

## 7. Third-Party Sub-Processors

We work with the following third-party sub-processors who may process your data on our behalf:

| Provider | Purpose | Data Processed | Status |
|---|---|---|---|
| Supabase | Database & Authentication | All user and trade data | Active |
| Anthropic | AI-powered analysis | Anonymized trade patterns (when launched) | Planned |

---

## 8. Your Rights

Depending on your location, you may have the following rights regarding your personal data:

### All Users
- **Access:** Request a copy of the data we hold about you.
- **Correction:** Request correction of inaccurate data.
- **Deletion:** Delete your account and all associated data directly from within the Service. To do this, sign in and go to **Profile Settings → Danger Zone → Delete my account**. You'll be asked to confirm by typing **DELETE**. Once confirmed:
  - Your account is marked for deletion immediately and you are signed out.
  - Your data is retained for a **30-day grace period** during which you may restore the account by signing back in with the same email.
  - After 30 days, your data is permanently and irreversibly deleted from our database, and your Stripe customer record is removed.
  - Billing records (invoices, payment receipts) are retained by our payment processor for the period required by applicable financial and tax law, even after account deletion.
  - If you had an active subscription, it is set to cancel at the end of your current billing period. No refunds are issued for unused time. See the Terms of Service for billing details.
- **Export:** Request an export of your trade data in a portable format (CSV). Available from Profile Settings → Data Management at any time, before or instead of deleting your account.

### European Union / EEA Users (GDPR)
In addition to the above, you have the right to:
- **Restrict Processing:** Request that we limit how we use your data.
- **Object to Processing:** Object to processing based on legitimate interests.
- **Data Portability:** Receive your data in a structured, machine-readable format.
- **Lodge a Complaint:** With your local data protection authority.

Our legal basis for processing your data is:
- **Contract:** Processing necessary to provide the Service you subscribed to.
- **Legitimate Interest:** Security monitoring and Service improvement.
- **Consent:** AI-powered features and any optional data processing.

### California Users (CCPA)
You have the right to:
- Know what personal information is collected and how it is used.
- Request deletion of your personal information.
- Opt out of the sale of personal information (we do not sell personal information).
- Non-discrimination for exercising your privacy rights.

---

## 9. Cookies and Local Storage

The Service uses browser local storage to maintain your authentication session. We do not use third-party advertising cookies. We may use minimal analytics cookies to understand aggregate usage patterns.

---

## 10. Children's Privacy

The Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe a minor has provided us with personal information, please contact us and we will delete it promptly.

---

## 11. Changes to This Policy

We may update this Privacy Policy from time to time. We will notify you of material changes by:
- Posting a notice within the Service
- Sending an email to your registered address
- Updating the "Last Updated" date at the top of this policy

Continued use of the Service after changes constitutes acceptance of the updated policy.

---

## 12. Contact Us

If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us at **admin@futures-os.app**.

For GDPR-related requests, please include "GDPR Request" in the subject line.
For CCPA-related requests, please include "CCPA Request" in the subject line.

We will respond to all requests within 30 days.`;

  const termsContent = `# Terms of Service

**Futures OS**
Last Updated: ${formatVersionLabel(TERMS_VERSION)}

---

## 1. Agreement to Terms

By accessing or using Futures OS ("Service," "we," "our," or "us"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.

These Terms constitute a legally binding agreement between you ("User," "you," or "your") and Futures OS. Please read them carefully.

---

## 2. Description of Service

Futures OS is a subscription-based trading journal and analytics platform designed to help traders track, analyze, and improve their trading performance. The Service includes:

- Trade journaling and logging tools
- Performance analytics and reporting
- Behavioral and discipline scoring
- AI-powered personalized trade analysis and daily briefings (planned — see Section 6)
- Strategy management tools
- Risk management settings and alerts
- CSV trade import from supported brokers

---

## 3. Eligibility

To use the Service, you must:

- Be at least 18 years of age
- Have the legal capacity to enter into a binding agreement
- Not be prohibited from using the Service under applicable law
- Provide accurate and complete registration information

By using the Service, you represent and warrant that you meet all eligibility requirements.

---

## 4. Account Registration and Security

### 4.1 Account Creation
You must create an account to use the Service. You agree to provide accurate, current, and complete information during registration and to keep this information updated.

### 4.2 Account Security
You are responsible for:
- Maintaining the confidentiality of your account credentials
- All activity that occurs under your account
- Notifying us immediately of any unauthorized access

We are not liable for any loss resulting from unauthorized use of your account.

### 4.3 One Account Per User
Each account is for a single individual user. You may not share your account credentials with others or allow others to access the Service through your account.

---

## 5. Subscription and Payment

### 5.1 Subscription Plans
Access to the Service requires a paid subscription.

### 5.2 Billing
- Subscriptions are billed in advance on a recurring basis (monthly or annually, depending on your selected plan)
- You authorize us to charge your payment method on each renewal date
- All fees are in US Dollars unless otherwise stated

### 5.3 Free Trial
If we offer a free trial, it will be described at sign-up. At the end of the trial period, your subscription will automatically convert to a paid plan unless you cancel before the trial ends.

### 5.4 Cancellation
You may cancel your subscription at any time, in either of two ways:

- **Through the Stripe Customer Portal**, accessible via the Billing page in the Service. This cancels your subscription only — your account, data, and settings remain intact, and you can re-subscribe later.
- **By deleting your account**, accessible via Profile Settings → Danger Zone. This cancels your subscription <em>and</em> initiates account deletion. See Section 17 for details.

In both cases:
- Cancellation takes effect at the end of your current billing period.
- You retain access to the Service until the end of the paid period.
- We do not provide refunds for partial billing periods unless required by law.

### 5.5 Refund Policy
- All subscription fees, monthly and annual, are **non-refundable** once paid. We do not pro-rate refunds for unused portions of a billing period, whether you cancel via the Stripe Customer Portal or by deleting your account.
- The only exception is where a refund is required by applicable consumer-protection law in your jurisdiction. In those cases, refund requests should be submitted to support at the address in Section 19.

### 5.6 Price Changes
We reserve the right to change subscription prices. We will provide at least 30 days advance notice of any price increase. Continued use after the notice period constitutes acceptance of the new pricing.

---

## 6. Acceptable Use

### 6.1 Permitted Use
You may use the Service solely for your personal trading journal and analytics purposes in accordance with these Terms.

### 6.2 Prohibited Use
You agree not to:

- Use the Service for any unlawful purpose or in violation of any regulations
- Share, resell, sublicense, or redistribute access to the Service
- Attempt to reverse engineer, decompile, or extract source code from the Service
- Use automated tools, bots, or scripts to access or scrape the Service
- Introduce malware, viruses, or malicious code
- Attempt to gain unauthorized access to any part of the Service or its infrastructure
- Use the Service to transmit spam or unsolicited communications
- Impersonate any person or entity
- Use the Service in a manner that could damage, disable, or impair it
- Circumvent any access controls or security measures

### 6.3 Account Termination for Violations
We reserve the right to suspend or terminate your account immediately and without notice if you violate these Terms.

---

## 7. Important Disclaimer — Not Financial Advice

**THE SERVICE IS A TRADING JOURNAL AND ANALYTICS TOOL ONLY.**

- Futures OS does **not** provide financial advice, investment advice, trading recommendations, or any form of regulated financial services
- AI-generated analysis and daily briefings are for **informational and educational purposes only** and do not constitute trading recommendations
- Past performance data shown in the Service does not guarantee future results
- Trading futures, options, and other financial instruments involves **substantial risk of loss** and is not suitable for all investors
- You should consult a qualified financial advisor before making any trading or investment decisions
- We are not registered as an investment advisor, broker-dealer, or financial planner with any regulatory authority

**You are solely responsible for your trading decisions and any resulting profits or losses.**

### 7.1 Prop Firm Disclaimer

Futures OS is an independent third-party tool and is **not affiliated with, endorsed by, or partnered with** any proprietary trading firm, including but not limited to Apex Trader Funding, TopStep, Bulenox, Take Profit Trader, MyFundedFutures, Earn2Trade, Uprofit, Leeloo Trading, EdgeClear, or TradeDay.

Drawdown tracking, challenge progress indicators, and account balance displays within the Service are provided for **informational and journaling purposes only**. These figures are derived from trade data you import or manually enter and may not reflect your actual funded account status, real-time balance, or compliance standing with any prop firm.

**You must always verify your drawdown, balance, and rule compliance directly with your prop firm's official platform.** Do not rely on Futures OS data to determine whether you are within your funded account rules. Relying solely on Futures OS figures for compliance decisions could result in account violations, suspension, or loss of funding that we are not responsible for.

### 7.2 Imported Data Accuracy

Trade data imported via CSV is only as accurate as the source file exported from your broker or platform. Futures OS does not independently verify, reconcile, or guarantee the accuracy of imported trade data. Discrepancies between imported data and your broker's official records may exist due to export format variations, partial fills, fee calculations, or timezone differences.

**Always cross-reference your Futures OS trade history against your broker's official statements before making any trading or financial decisions.**

---

## 8. AI-Powered Features (Planned — Not Currently Active)

### 8.1 Nature of AI Analysis
The Service is being developed to include AI-powered features that will generate personalized analysis using your trade data. These features are not currently active. When launched, they:

- Will be powered by Anthropic's Claude AI model
- Will use anonymized versions of your trade history and behavioral data
- Will generate informational insights, not financial advice
- May occasionally produce inaccurate or incomplete analysis

### 8.2 No Guarantee of Accuracy
When AI features become available, AI-generated content will be provided "as is." We make no warranty that AI analysis will be accurate, complete, or suitable for any particular purpose. You should independently verify any AI-generated insights before acting on them.

### 8.3 Opt-Out
When AI features launch, you will be able to disable AI-powered features at any time in your account settings. When disabled, your data will not be sent to third-party AI processors. We will not begin sending data to AI processors without an in-product notice.

---

## 9. Intellectual Property

### 9.1 Our Property
The Service, including its design, code, features, logos, and content (excluding your data), is owned by Futures OS and protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written consent.

### 9.2 Your Data
You retain full ownership of all trade data, journal entries, and other content you submit to the Service ("Your Data"). By using the Service, you grant us a limited, non-exclusive license to store, process, and display Your Data solely for the purpose of providing the Service to you.

### 9.3 Feedback
If you submit feedback, suggestions, or ideas about the Service, you grant us the right to use that feedback without compensation or attribution to you.

---

## 10. Privacy

Your use of the Service is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to our collection and use of your data as described in the Privacy Policy.

---

## 11. Third-Party Services

The Service integrates with third-party services including Supabase (database) and, when AI features launch, Anthropic (AI). Your use of the Service is subject to the terms and privacy policies of these third-party providers. We are not responsible for the practices of third-party services.

---

## 12. Service Availability and Modifications

### 12.1 Availability
We strive to maintain high availability but do not guarantee uninterrupted access to the Service. We may experience downtime due to maintenance, updates, or circumstances beyond our control.

### 12.2 Modifications
We reserve the right to modify, suspend, or discontinue any feature of the Service at any time. We will provide reasonable notice of material changes where practicable.

### 12.3 Data Export
If we discontinue the Service, we will provide you with at least 30 days notice and the ability to export your trade data before shutdown.

---

## 13. Disclaimers and Limitation of Liability

### 13.1 Disclaimer of Warranties
THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

### 13.2 Limitation of Liability
TO THE MAXIMUM EXTENT PERMITTED BY LAW:

- WE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES
- WE WILL NOT BE LIABLE FOR ANY TRADING LOSSES, LOST PROFITS, OR FINANCIAL LOSSES OF ANY KIND ARISING FROM YOUR USE OF THE SERVICE
- OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING FROM THESE TERMS OR THE SERVICE WILL NOT EXCEED THE AMOUNT YOU PAID US IN THE 3 MONTHS PRECEDING THE CLAIM

### 13.3 Essential Basis
You acknowledge that the limitations of liability in this section are an essential element of the agreement between you and Futures OS, without which we would not provide the Service.

---

## 14. Indemnification

You agree to indemnify, defend, and hold harmless Futures OS and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable legal fees) arising from:

- Your use of the Service
- Your violation of these Terms
- Your violation of any third-party rights
- Any trading decisions made based on information from the Service

---

## 15. Governing Law and Dispute Resolution

### 15.1 Governing Law
These Terms are governed by the laws of the United States, without regard to conflict of law principles.

### 15.2 Informal Resolution
Before filing any legal claim, you agree to contact us and attempt to resolve the dispute informally for at least 30 days.

### 15.3 Arbitration
If informal resolution fails, disputes will be resolved through binding arbitration under the rules of the American Arbitration Association (AAA). You waive the right to a jury trial and to participate in class action lawsuits.

### 15.4 Exception
Either party may seek injunctive or equitable relief in a court of competent jurisdiction for claims involving intellectual property or unauthorized access.

---

## 16. Changes to Terms

We may update these Terms from time to time. We will notify you of material changes by:

- Posting a notice within the Service
- Sending an email to your registered address at least 14 days before changes take effect
- Updating the "Last Updated" date at the top of this document

Continued use of the Service after the effective date constitutes acceptance of the updated Terms. If you do not agree to the changes, you must cancel your subscription before the effective date.

---

## 17. Termination

### 17.1 By You
You may terminate your account at any time by signing in and going to **Profile Settings → Danger Zone → Delete my account**. You will be asked to confirm by typing **DELETE**. Termination is effective immediately upon confirmation, with the following consequences:

- **Sign-out:** You are signed out of all sessions across all devices.
- **Subscription cancellation:** Any active subscription is set to cancel at the end of your current billing period. You retain access to paid features until that date. No refunds are issued for unused time.
- **30-day grace period:** Your trades, accounts, custom strategies, settings, and history are retained for 30 days. During this period you may restore your account by signing back in with the same email and password.
- **Permanent deletion:** After 30 days have elapsed, your account and all associated data are permanently and irreversibly deleted, including your Stripe customer record. Billing records may be retained by our payment processor as required by applicable tax and financial law.

You may also cancel your subscription without deleting your account, through the Stripe Customer Portal accessible from the Billing page. See Section 5.4.

### 17.2 By Us
We may suspend or terminate your account immediately if you:
- Violate these Terms
- Fail to pay subscription fees
- Engage in fraudulent or harmful behavior

### 17.3 Effect of Termination
Upon termination by either party, your right to use the Service ceases according to the timeline above. You may export your trade data as a CSV from Profile Settings before initiating termination, or during the 30-day grace period if you initiated termination yourself.

---

## 18. Miscellaneous

### 18.1 Entire Agreement
These Terms and our Privacy Policy constitute the entire agreement between you and Futures OS regarding the Service.

### 18.2 Severability
If any provision of these Terms is found unenforceable, the remaining provisions will continue in full force.

### 18.3 No Waiver
Our failure to enforce any provision of these Terms does not constitute a waiver of our right to enforce it in the future.

### 18.4 Assignment
You may not assign your rights under these Terms without our consent. We may assign our rights to a successor in connection with a merger or acquisition.

---

## 19. Contact Information

For questions about these Terms, please contact us at **admin@futures-os.app**.

For legal notices, please use "Legal Notice" in the subject line.`;

  const content = title === "Privacy Policy" ? privacyContent : termsContent;

  return (
    <div style={{ fontSize: 12, lineHeight: 1.6, color: C.text, padding: 16, whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
      {content}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LOGIN SCREEN
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// LOGIN SCREEN - WITH PASSWORD VISIBILITY TOGGLE
// ─────────────────────────────────────────────────────────────



